/**
 * ForgotPassword: request a reset link. Always reports success (the backend
 * never reveals whether the email is registered). With ?staff=1 it resets
 * this city's staff account instead of a resident account.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { requestPasswordReset } from '../lib/auth';
import { AuthField, AuthShell, AuthSubmit } from '../components/AuthShell';

export function ForgotPassword(): JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const staff = params.get('staff') === '1';
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestPasswordReset(email.trim(), { staff });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('auth.forgotFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title={staff ? t('auth.staffForgotTitle') : t('auth.forgotTitle')}
      subtitle={t('auth.forgotSubtitle')}
    >
      {done ? (
        <p role="status" className="text-sm text-stone-700">
          {t('auth.forgotDone')}
        </p>
      ) : (
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <AuthField
            label={t('auth.email')}
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
          <AuthSubmit busy={busy} busyLabel={t('auth.sending')}>
            {t('auth.sendReset')}
          </AuthSubmit>
        </form>
      )}
      <p className="mt-4 text-center text-sm text-stone-600">
        <Link to={staff ? '/admin' : '/signin'} className="font-medium text-tenant-primary hover:underline">
          {staff ? t('auth.staffBackToSignIn') : t('auth.backToSignIn')}
        </Link>
      </p>
    </AuthShell>
  );
}
